<template>
  <section class="field-list--outline">
    <div class="field-list">
      <div class="field pr-0">
        <label
          :for="`${membershipUuid}_address_${index}_type`"
          v-text="'Address Type'"
        />
        <div class="select-container">
          <select
            :id="`${membershipUuid}_address_${index}_type`"
            :name="`${membershipUuid}_address_${index}_type`"
            :value="address.type_id"
            @change="update('type_id', $event.target.value)"
          >
            <option
              v-for="type in addressTypes"
              :key="`address-type-${type.id}`"
              :value="type.id"
              v-text="type.label"
            />
          </select>
        </div>
      </div>
    </div>
    <div class="field-list">
      <div class="field sm:w-1/2 pr-0 sm:pr-2">
        <label
          :for="`${membershipUuid}_address_${index}_address_line_1`"
          v-text="'Street Address 1'"
        />
        <input
          :id="`${membershipUuid}_address_${index}_address_line_1`"
          :name="`${membershipUuid}_address_${index}_address_line_1`"
          :value="address.address_line_1"
          :class="{ error: errors.has(`addresses.${index}.address_line_1`) }"
          type="text"
          placeholder="Address Line 1"
          @input="update('address_line_1', $event.target.value)"
        >
        <div class="field__meta">
          <span
            v-if="errors.has(`addresses.${index}.address_line_1`)"
            class="field__meta__help"
            v-text="errors.get(`addresses.${index}.address_line_1`)"
          />
        </div>
      </div>
      <div class="field sm:w-1/2 pr-0 sm:pl-2">
        <label
          :for="`${membershipUuid}_address_${index}_address_line_2`"
          v-text="'Street Address 2'"
        />
        <input
          :id="`${membershipUuid}_address_${index}_address_line_2`"
          :name="`${membershipUuid}_address_${index}_address_line_2`"
          :value="address.address_line_2"
          type="text"
          placeholder="Address Line 2"
          @input="update('address_line_2', $event.target.value)"
        >
      </div>
    </div>
    <div class="field-list">
      <div class="field sm:w-1/4 pr-0 sm:pr-2">
        <label
          :for="`${membershipUuid}_address_${index}_city`"
          v-text="'Town / City'"
        />
        <input
          :id="`${membershipUuid}_address_${index}_city`"
          :name="`${membershipUuid}_address_${index}_city`"
          :value="address.city"
          :class="{ error: errors.has(`addresses.${index}.city`) }"
          type="text"
          placeholder="Town / City"
          @input="update('city', $event.target.value)"
        >
        <div class="field__meta">
          <span
            v-if="errors.has(`addresses.${index}.city`)"
            class="field__meta__help"
            v-text="errors.get(`addresses.${index}.city`)"
          />
        </div>
      </div>
      <div class="field sm:w-1/4 pr-0 sm:px-2">
        <label
          :for="`${membershipUuid}_address_${index}_state`"
          v-text="'State / Province'"
        />
        <div class="select-container">
          <select
            :id="`${membershipUuid}_address_${index}_state`"
            :name="`${membershipUuid}_address_${index}_state`"
            :value="address.state"
            :class="{ error: errors.has(`addresses.${index}.state`) }"
            @change="update('state', $event.target.value)"
          >
            <option
              value=""
              v-text="'Select'"
            />
            <option
              v-for="state in states"
              :key="`state-${state.shortCode}`"
              :value="state.shortCode"
              v-text="state.name"
            />
          </select>
        </div>
        <div class="field__meta">
          <span
            v-if="errors.has(`addresses.${index}.state`)"
            class="field__meta__help"
            v-text="errors.get(`addresses.${index}.state`)"
          />
        </div>
      </div>
      <div class="field sm:w-1/4 pr-0 sm:px-2">
        <label
          :for="`${membershipUuid}_address_${index}_zip`"
          v-text="'Zip / Postcode'"
        />
        <input
          :id="`${membershipUuid}_address_${index}_zip`"
          :name="`${membershipUuid}_address_${index}_zip`"
          :value="address.zip"
          type="text"
          placeholder="ZIP"
          :class="{ error: errors.has(`addresses.${index}.zip`) }"
          @input="update('zip', $event.target.value)"
        >
        <div class="field__meta">
          <span
            v-if="errors.has(`addresses.${index}.zip`)"
            class="field__meta__help"
            v-text="errors.get(`addresses.${index}.zip`)"
          />
        </div>
      </div>
      <div class="field sm:w-1/4 pr-0 sm:pl-2">
        <label
          :for="`${membershipUuid}_country`"
          v-text="'Country'"
        />
        <div class="select-container">
          <select
            :id="`${membershipUuid}_country`"
            :name="`${membershipUuid}_country`"
            :value="address.country"
            :class="{ error: errors.has(`addresses.${index}.country`) }"
            @change="update('country', $event.target.value)"
          >
            <option
              v-for="country in countries"
              :key="country.code"
              :value="country.countryShortCode"
              v-text="country.countryName"
            />
          </select>
        </div>
        <div class="field__meta">
          <span
            v-if="errors.has(`addresses.${index}.country`)"
            class="field__meta__help"
            v-text="errors.get(`addresses.${index}.country`)"
          />
        </div>
      </div>
    </div>
    <div class="w-full flex justify-between">
      <div>
        <button
          type="button"
          class="link-plain text-sm"
          @click.prevent="$emit('add')"
          v-text="'Add another'"
        />
        <span
          class="text-grey mx-1"
          v-text="'|'"
        />
        <button
          type="button"
          class="link-plain text-sm"
          @click.prevent="$emit('remove')"
          v-text="'Remove'"
        />
      </div>
      <div class="flex">
        <AppCheckboxInput
          v-if="profileSharingIsEnabled"
          :id="`${membershipUuid}_address_${index}_is_visible`"
          v-model="address.is_visible"
          :name="`${membershipUuid}_address_${index}_is_visible`"
          label="Visible?"
        />
      </div>
    </div>
  </section>
</template>

<script type="text/babel">
import _ from 'lodash'
import { mapState } from 'vuex'
import countries from './countries'
import HasErrors from 'Base/Resources/assets/js/mixins/has-errors'
import AppCheckboxInput from 'Base/Resources/assets/js/components/forms/inputs/AppCheckboxInput'

export default {
  name: 'AddressForm',
  components: {
    AppCheckboxInput,
  },
  mixins: [HasErrors],
  props: {
    value: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    membershipUuid: {
      type: String,
      required: false,
      default: 'member',
    },
    profileSharingIsEnabled: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      countries,
    }
  },
  computed: {
    ...mapState({
      addressTypes: state => state.membership.addressTypes,
    }),
    address() {
      return this.value
    },
    states() {
      return _.find(this.countries, country => country.countryShortCode === (this.address.country || 'US')).regions
    },
  },
  methods: {
    update(key, value) {
      this.$emit('input', { ...this.address, [key]: value })
    },
  },
}
</script>
